/**
 * RegistrationService (AJIP-16.02.005 · WS3)
 * ---------------------------------------------------------------------
 * Wraps the protected AJIP-16.02.003 JourneyRegistry. Direct mutation
 * of internal registry state is prohibited (WS3). The service adds:
 *   * Extended validation via DefinitionValidator
 *   * Audit log
 *   * Version-controlled replacement rules
 *   * Fail-closed feature-flag gating
 */
import { isFeatureEnabled } from '../../lib/featureFlags';
import { FlagDisabledError } from '../core/JourneyErrors';
import { JourneyRegistry } from '../core/JourneyRegistry';
import { validateExtendedDefinition, toBaseDefinition } from './DefinitionValidator';
import { assertNotDeprecated, canReplace } from './VersioningPolicy';
import {
  DuplicateJourneyIdError,
  RegistrationConflictError,
} from './RegistrationDiscoveryErrors';
import {
  REGISTRATION_DISCOVERY_EVENTS,
  RegistrationDiscoveryObservability,
} from './RegistrationDiscoveryObservability';

const _requireEnabled = (svc) => {
  if (!svc._enabled) throw new FlagDisabledError('JOURNEYS_ENABLED is false. RegistrationService is inert.');
};

class RegistrationService {
  constructor({ registry, observability, nowFn = () => Date.now() } = {}) {
    this._enabled = isFeatureEnabled('JOURNEYS_ENABLED');
    this._registry = registry instanceof JourneyRegistry ? registry : new JourneyRegistry();
    this._observability = observability instanceof RegistrationDiscoveryObservability
      ? observability
      : new RegistrationDiscoveryObservability();
    this._extended = new Map(); // journeyId -> extended definition (frozen)
    this._audit = [];
    this._now = nowFn;
  }

  get enabled() { return this._enabled; }
  get registry() { return this._registry; }
  get observability() { return this._observability; }

  _record(entry) {
    this._audit.push(Object.freeze({ at: this._now(), ...entry }));
  }

  register(definition) {
    _requireEnabled(this);
    this._observability.emit(REGISTRATION_DISCOVERY_EVENTS.JOURNEY_REGISTRATION_REQUESTED, {
      journeyId: definition && definition.journeyId,
    });
    try {
      validateExtendedDefinition(definition);
      this._observability.emit(REGISTRATION_DISCOVERY_EVENTS.JOURNEY_DEFINITION_VALIDATED, {
        journeyId: definition.journeyId,
      });
    } catch (e) {
      this._observability.emit(REGISTRATION_DISCOVERY_EVENTS.JOURNEY_DEFINITION_INVALID, {
        journeyId: definition && definition.journeyId, error: e.name, message: e.message,
      });
      this._observability.emit(REGISTRATION_DISCOVERY_EVENTS.JOURNEY_REGISTRATION_REJECTED, {
        journeyId: definition && definition.journeyId, error: e.name,
      });
      this._record({ action: 'register', ok: false, journeyId: definition && definition.journeyId, error: e.name });
      throw e;
    }

    if (this._extended.has(definition.journeyId)) {
      const existing = this._extended.get(definition.journeyId);
      if (!canReplace(existing, definition)) {
        this._observability.emit(REGISTRATION_DISCOVERY_EVENTS.REGISTRATION_CONFLICT_DETECTED, {
          journeyId: definition.journeyId,
          existingVersion: existing.journeyVersion,
          incomingVersion: definition.journeyVersion,
        });
        this._observability.emit(REGISTRATION_DISCOVERY_EVENTS.JOURNEY_REGISTRATION_REJECTED, {
          journeyId: definition.journeyId, error: 'DuplicateJourneyIdError',
        });
        this._record({ action: 'register', ok: false, journeyId: definition.journeyId, error: 'duplicate' });
        throw new DuplicateJourneyIdError(`Journey '${definition.journeyId}' already registered at same or newer version`);
      }
      // Version bump — unregister previous base definition, then re-register.
      this._registry.unregister(definition.journeyId);
    }

    // Register the base engine definition through the protected registry.
    this._registry.register(toBaseDefinition(definition));
    const frozen = Object.freeze({ ...definition });
    this._extended.set(definition.journeyId, frozen);
    this._observability.emit(REGISTRATION_DISCOVERY_EVENTS.JOURNEY_REGISTRATION_COMPLETED, {
      journeyId: definition.journeyId, version: definition.journeyVersion,
    });
    this._record({ action: 'register', ok: true, journeyId: definition.journeyId, version: definition.journeyVersion });
    return frozen;
  }

  registerMany(definitions) {
    _requireEnabled(this);
    const results = [];
    for (const d of definitions || []) {
      try { results.push({ ok: true, definition: this.register(d) }); }
      catch (e) { results.push({ ok: false, journeyId: d && d.journeyId, error: e.name, message: e.message }); }
    }
    return results;
  }

  validateBeforeRegistration(definition) {
    _requireEnabled(this);
    validateExtendedDefinition(definition);
    return true;
  }

  replace(definition) {
    _requireEnabled(this);
    validateExtendedDefinition(definition);
    if (!this._extended.has(definition.journeyId)) {
      throw new RegistrationConflictError(`No existing journey '${definition.journeyId}' to replace`);
    }
    const existing = this._extended.get(definition.journeyId);
    if (!canReplace(existing, definition)) {
      throw new RegistrationConflictError('Replacement must have a strictly greater version');
    }
    this._registry.unregister(definition.journeyId);
    this._registry.register(toBaseDefinition(definition));
    const frozen = Object.freeze({ ...definition });
    this._extended.set(definition.journeyId, frozen);
    this._record({ action: 'replace', ok: true, journeyId: definition.journeyId, version: definition.journeyVersion });
    return frozen;
  }

  unregister(journeyId) {
    _requireEnabled(this);
    if (typeof journeyId !== 'string') throw new Error('journeyId must be a string');
    const had = this._extended.delete(journeyId);
    if (had) this._registry.unregister(journeyId);
    this._record({ action: 'unregister', ok: had, journeyId });
    return had;
  }

  getRegistrationStatus(journeyId) {
    if (!this._enabled) return null;
    if (!this._extended.has(journeyId)) return null;
    const def = this._extended.get(journeyId);
    return Object.freeze({
      journeyId: def.journeyId,
      journeyVersion: def.journeyVersion,
      registrationStatus: def.registrationStatus,
      deprecationStatus: def.deprecationStatus,
      updatedAt: def.updatedAt,
    });
  }

  listRegisteredJourneys() {
    if (!this._enabled) return Object.freeze([]);
    return Object.freeze(Array.from(this._extended.values()));
  }

  assertNotDeprecated(journeyId) {
    _requireEnabled(this);
    if (!this._extended.has(journeyId)) return true;
    return assertNotDeprecated(this._extended.get(journeyId));
  }

  auditLog() { return Object.freeze(this._audit.slice()); }
}

export { RegistrationService };
